<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => { },
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      scope: 'global',
      selectedPipelineId: null,
      selectedStageId: null,
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      pipelines: 'crmPipelines/getPipelines',
    }),
    selectedPipelineStages() {
      if (!this.selectedPipelineId) return [];
      const pipeline = this.pipelines.find(p => p.id === Number(this.selectedPipelineId));
      return pipeline?.stages || [];
    },
  },
  mounted() {
    this.$store.dispatch('crmPipelines/get');
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.scope = 'global';
      this.selectedPipelineId = null;
      this.selectedStageId = null;
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          scope: this.scope,
          crm_pipeline_stage_id: this.selectedStageId || null,
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="$t('CANNED_MGMT.ADD.TITLE')" :header-content="$t('CANNED_MGMT.ADD.DESC')" />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input v-model="shortCode" type="text" :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch" />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor v-model="content" class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }" channel-type="Context::Default" enable-variables
              :enable-canned-responses="false" :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch" />
          </div>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.SCOPE.LABEL') }}
          </label>
          <div class="flex gap-4 mb-4">
            <label class="flex items-center gap-2 cursor-pointer text-sm">
              <input v-model="scope" type="radio" value="global" class="w-4 h-4" />
              {{ $t('CANNED_MGMT.SCOPE.GLOBAL') }}
            </label>
            <label class="flex items-center gap-2 cursor-pointer text-sm">
              <input v-model="scope" type="radio" value="personal" class="w-4 h-4" />
              {{ $t('CANNED_MGMT.SCOPE.PERSONAL') }}
            </label>
          </div>
        </div>

        <div class="w-full">
          <label>
            Vincular a etapa do pipeline (opcional)
          </label>
          <p class="text-xs text-n-slate-10 mb-2">
            Se vinculada, esta resposta só aparecerá no chat quando a conversa estiver nesta etapa.
          </p>
          <div class="flex gap-3 mb-0">
            <select
              v-model="selectedPipelineId"
              @change="selectedStageId = null"
            >
              <option :value="null">Nenhum pipeline</option>
              <option
                v-for="p in pipelines"
                :key="p.id"
                :value="p.id"
              >
                {{ p.name }}
              </option>
            </select>
            <select
              v-if="selectedPipelineId"
              v-model="selectedStageId"
            >
              <option :value="null">Todas as etapas</option>
              <option
                v-for="s in selectedPipelineStages"
                :key="s.id"
                :value="s.id"
              >
                {{ s.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton faded slate type="reset" :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose" />
          <NextButton type="submit" :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')" :disabled="v$.content.$invalid ||
            v$.shortCode.$invalid ||
            addCanned.showLoading
            " :is-loading="addCanned.showLoading" />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
